import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { supabase } from "@/integrations/supabase/client";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as currency with thousand separators
 * @param amount - The amount to format
 * @param currency - The currency symbol (default: 'R')
 * @returns Formatted currency string (e.g., "R1 000,50")
 */
export function formatCurrency(amount: number, currency: string = 'R'): string {
  return `${currency}${amount.toLocaleString('en-ZA', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
}

/**
 * Checks if an email already exists for another customer in the same company
 */
export async function checkDuplicateEmail(email: string, customerId?: string): Promise<boolean> {
  try {
    if (!email || email.trim() === '') return false;
    
    const { data: profile } = await supabase.auth.getUser();
    if (!profile?.user) return false;
    
    // Get all customers
    const { data: customers } = await supabase
      .from('customers')
      .select('id, emails');
    
    if (!customers || customers.length === 0) return false;
    
    // Filter the results to check for the email
    const duplicates = customers.filter(customer => {
      // Skip the current customer if we're editing
      if (customerId && customer.id === customerId) return false;
      
      // Check if the customer's emails array contains the email we're looking for
      return customer.emails && customer.emails.some(e => 
        e.toLowerCase() === email.toLowerCase()
      );
    });
    
    return duplicates.length > 0;
  } catch (error) {
    console.error("Error checking duplicate email:", error);
    return false;
  }
}

/**
 * Checks if a phone number already exists for another customer in the same company
 */
export async function checkDuplicatePhoneNumber(phoneNumber: string, customerId?: string): Promise<boolean> {
  try {
    if (!phoneNumber || phoneNumber.trim() === '') return false;
    
    const { data: profile } = await supabase.auth.getUser();
    if (!profile?.user) return false;
    
    // Get all customers
    const { data: customers } = await supabase
      .from('customers')
      .select('id, cell_numbers');
    
    if (!customers || customers.length === 0) return false;
    
    // Filter the results to check for the phone number
    const duplicates = customers.filter(customer => {
      // Skip the current customer if we're editing
      if (customerId && customer.id === customerId) return false;
      
      // Check if the customer's cell_numbers array contains the phone number we're looking for
      return customer.cell_numbers && customer.cell_numbers.some(n => {
        // Normalize phone numbers for comparison by removing non-numeric characters
        const normalizedInput = phoneNumber.replace(/\D/g, '');
        const normalizedStored = n.replace(/\D/g, '');
        return normalizedStored === normalizedInput;
      });
    });
    
    return duplicates.length > 0;
  } catch (error) {
    console.error("Error checking duplicate phone number:", error);
    return false;
  }
}
